import { ModalName, ModalNames } from 'structure/staking/types';
import React from 'react';
import { StakingPool, UserPoolInfo } from 'api/StakingAPI';
import { ethers } from 'ethers';
import { useTranslation } from 'react-i18next';
import { UserContext, useUser } from 'structure/user/UserContext';
import { useAppDispatch } from 'store/hooks';
import { useConnection, useSwitchChain, useChainId } from 'wagmi';
import { selectChain } from 'store/features/chain/chainActions';
import { NetworkChainIds } from '@dapp/multi-lib/network';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { ConnectButton } from 'components/ConnectButton';

type StakingPoolActions = {
  pool: StakingPool;
  userPoolInfo: UserPoolInfo;
  onOpenModal: (modalName: ModalName, poolId: number) => void;
};

export function StakingPoolActions({
  pool,
  userPoolInfo,
  onOpenModal,
}: StakingPoolActions) {
  const { t } = useTranslation('common');
  const account = useConnection();
  const chainId = useChainId() as NetworkChainIds;

  const { connector } = useUser() as UserContext;
  const dispatch = useAppDispatch();
  const isNetworkValid = pool?.chainId === chainId;
  const { switchChainAsync } = useSwitchChain();

  const handleNetworkChange = async () => {
    await switchChainAsync?.({ chainId: pool?.chainId });
    dispatch(
      selectChain({
        newChainId: pool?.chainId,
        connector,
        currentChainId: chainId,
      })
    );
  };

  const bnTotalStaked = ethers.BigNumber.from(userPoolInfo?.totalStaked || 0);
  // const bnClaimable = ethers.BigNumber.from(userPoolInfo?.claimable || 0);

  const disabled = pool?.depositTokenSymbol === 'SDAO';

  const handleActionClick = (action: () => void) => async () => {
    if (!isNetworkValid) {
      try {
        await handleNetworkChange();
        action();
      } catch (error) {
        console.log('### error', error);
      }
    } else {
      action();
    }
  };

  if (!account.isConnected) {
    return (
      <div className='flex w-full flex-row justify-end'>
        <ConnectButton />
      </div>
    );
  }

  return (
    <div className='flex w-full flex-row items-center justify-end gap-2'>
      {isNetworkValid ? (
        <>
          <Button
            size='small'
            style='outline'
            onClick={handleActionClick(() =>
              onOpenModal(ModalNames.CLAIM, pool?.id)
            )}
          >
            {t('STAKING.CLAIM')}
          </Button>

          {bnTotalStaked.isZero() ? (
            <Button
              disabled={disabled}
              onClick={handleActionClick(() =>
                onOpenModal(ModalNames.STAKE, pool?.id)
              )}
            >
              {t('STAKING.STAKE')}
            </Button>
          ) : (
            <>
              <Button
                size='small'
                style='outline'
                onClick={handleActionClick(() =>
                  onOpenModal(ModalNames.WITHDRAW, pool?.id)
                )}
              >
                {t('STAKING.WITHDRAW')}
              </Button>
              <Button
                onClick={handleActionClick(() =>
                  onOpenModal(ModalNames.STAKE_MORE, pool?.id)
                )}
              >
                {t('STAKING.MANAGE')}
              </Button>
            </>
          )}
        </>
      ) : (
        <Button onClick={handleNetworkChange}>{t('SWITCH_NETWORK')}</Button>
      )}
    </div>
  );
}
